// src/pages/SellerSuccess/SellerSuccess.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { sellerSuccessStyles, sellerSuccessAnimations } from './styles';
import StatusRenderer from './StatusRenderer';

const SellerSuccess = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Wait for Firebase Auth to initialize properly
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      if (!currentUser) {
        // Store the intended destination before redirecting to login
        sessionStorage.setItem('redirectAfterLogin', '/seller-success');
        navigate('/login');
        return;
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (authLoading || !user) {
      return;
    }

    // Verify the seller account setup was completed
    const verifySellerSetup = async () => {
      try {
        let result;
        
        // Development mode: Mock seller status verification
        if (process.env.REACT_APP_USE_EMULATORS === "true") {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Mock successful seller verification
          result = {
            data: {
              status: 'active',
              accountId: `acct_mock_${Date.now()}`
            }
          };
        } else {
          // Production mode: Call actual Firebase Function
          const { httpsCallable } = await import('firebase/functions');
          const { functions } = await import('../../services/firebase');
          
          const getSellerStatus = httpsCallable(functions, 'getSellerStatus');
          result = await getSellerStatus();
        }

        // Check if seller account is active
        if (result.data.status === 'active') {
          setStatus('success');
          
          // Update user status in Firestore (for development mode)
          if (process.env.REACT_APP_USE_EMULATORS === "true") {
            try {
              const { doc, updateDoc } = await import('firebase/firestore');
              const { db } = await import('../../services/firebase');
              
              const userRef = doc(db, 'users', user.uid);
              await updateDoc(userRef, {
                sellerStatus: 'active',
                stripeConnectAccountId: result.data.accountId,
                sellerOnboardingCompleted: new Date(),
              });
            } catch (dbError) {
            }
          }
          
          // Redirect to add listing page after showing success
          setTimeout(() => {
            navigate('/add-listing');
          }, 3000);
        } else if (result.data.status === 'pending' || result.data.status === 'restricted') {
          // Account created but not fully onboarded
          setError('Your account setup is incomplete. Please complete all required steps in Stripe.');
          setStatus('error');
        } else {
          setError('Account verification failed. Please try setting up your seller account again.');
          setStatus('error');
        }

      } catch (err) {
        setError(err.message || 'Failed to verify seller setup');
        setStatus('error');
      }
    };

    verifySellerSetup();
  }, [user, navigate, authLoading]);

  return (
    <div style={sellerSuccessStyles.page}>
      <div style={sellerSuccessStyles.container}>
        <StatusRenderer status={status} error={error} />
      </div>
      
      <style jsx>{sellerSuccessAnimations}</style>
    </div>
  );
};

export default SellerSuccess;